import { useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';

import { useAuthStore } from './authStore';
import { setUnauthenticatedHandler } from '@/api/client';
import { realtime } from '@/hooks/useWebSocket';
import AppLayout from '@/components/layout/AppLayout';
import LoginPage from '@/features/auth/LoginPage';
import { routes } from './routes';
import RequirePermission from '@/components/common/RequirePermission';
import NotificationHost from '@/components/common/NotificationHost';

export default function App() {
  const status = useAuthStore((state) => state.status);
  const restore = useAuthStore((state) => state.restore);
  const logout = useAuthStore((state) => state.logout);

  // recover the session from the refresh cookie before deciding what to render
  useEffect(() => {
    void restore();
  }, [restore]);

  // a refresh that fails for good drops the user back to the login screen
  useEffect(() => {
    setUnauthenticatedHandler(() => {
      void logout();
    });
    return () => setUnauthenticatedHandler(null);
  }, [logout]);

  // the socket follows the session
  useEffect(() => {
    if (status === 'authenticated') {
      realtime.connect();
      return () => realtime.disconnect();
    }
    return undefined;
  }, [status]);

  if (status === 'idle' || status === 'loading') {
    return (
      <Box className="flex h-full items-center justify-center">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <NotificationHost />
      <Routes>
        <Route
          path="/login"
          element={status === 'authenticated' ? <Navigate to="/" replace /> : <LoginPage />}
        />
        <Route
          path="/"
          element={status === 'authenticated' ? <AppLayout /> : <Navigate to="/login" replace />}
        >
          {routes.map((route) => (
            <Route
              key={route.path}
              index={route.index}
              path={route.index ? undefined : route.path}
              element={
                route.permission ? (
                  <RequirePermission permission={route.permission}>
                    {route.element}
                  </RequirePermission>
                ) : (
                  route.element
                )
              }
            />
          ))}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </>
  );
}
